import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  Spinner,
  Tabs,
  Tab,
  Chip,
} from '@nextui-org/react';
import {
  ArrowLeft,
  BarChart3,
  Swords,
  GitBranch,
  Flame,
  Target,
  TrendingUp,
  Trophy,
  Info,
} from 'lucide-react';
import statsExplorerService, {
  SeasonStats,
  ExplorerSeasonResultsResponse,
  ExplorerSeasonResult,
  SeasonDraw,
  SeasonPlayerStat,
  DrawMatch,
} from '../services/statsExplorerService';
import StatCard from '../components/stats/StatCard';
import LeaderboardTable from '../components/stats/LeaderboardTable';
import HighFinishBars from '../components/stats/HighFinishBars';

const TEAL = '#3fced1';

// ============================================================
// SeasonStatsPage – /statistiken/saison/:seasonStageId
// Tabs: Statistiken | Ergebnisse | Bracket
// Daten werden pro Tab lazy geladen (Stats sofort).
// ============================================================

const SeasonStatsPage: React.FC = () => {
  const { seasonStageId } = useParams<{ seasonStageId: string }>();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState<string>('stats');

  const [stats, setStats] = useState<SeasonStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const [resultsData, setResultsData] = useState<ExplorerSeasonResultsResponse | null>(null);
  const [loadingResults, setLoadingResults] = useState(false);

  const [draw, setDraw] = useState<SeasonDraw | null>(null);
  const [drawLoaded, setDrawLoaded] = useState(false);
  const [loadingDraw, setLoadingDraw] = useState(false);

  // Statistiken sofort laden
  useEffect(() => {
    if (!seasonStageId) return;
    let cancelled = false;
    (async () => {
      setLoadingStats(true);
      const data = await statsExplorerService.getSeasonStats(seasonStageId);
      if (cancelled) return;
      setStats(data);
      setLoadingStats(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [seasonStageId]);

  // Ergebnisse lazy laden
  useEffect(() => {
    if (selectedTab !== 'results' || resultsData || !seasonStageId) return;
    let cancelled = false;
    (async () => {
      setLoadingResults(true);
      const data = await statsExplorerService.getSeasonResults(seasonStageId);
      if (cancelled) return;
      setResultsData(data);
      setLoadingResults(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTab, resultsData, seasonStageId]);

  // Bracket lazy laden
  useEffect(() => {
    if (selectedTab !== 'draw' || drawLoaded || !seasonStageId) return;
    let cancelled = false;
    (async () => {
      setLoadingDraw(true);
      const data = await statsExplorerService.getSeasonDraw(seasonStageId);
      if (cancelled) return;
      setDraw(data);
      setDrawLoaded(true);
      setLoadingDraw(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTab, drawLoaded, seasonStageId]);

  // Spieler-Name → Spieler-Explorer (über Suche auflösen)
  const handlePlayerClick = useCallback(
    async (name: string) => {
      const clean = statsExplorerService.cleanPlayerName(name);
      if (clean === 'Unbekannt') return;
      const players = await statsExplorerService.searchPlayers(clean, 5);
      const match = players.find((p) => p.flashliveId);
      if (match?.flashliveId) {
        navigate(`/statistiken/spieler/${encodeURIComponent(match.flashliveId)}`);
      } else {
        navigate(`/statistiken?q=${encodeURIComponent(clean)}`);
      }
    },
    [navigate],
  );

  const title = draw?.tournamentName
    ? `${draw.tournamentName}${draw.seasonName ? ` ${draw.seasonName}` : ''}`
    : 'Turnier-Saison';

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>{title} – Saison-Statistiken | DartCloud</title>
        <meta
          name="description"
          content="Saison-Statistiken im DartCloud Statistik-Explorer: 180er, High Finishes, beste Averages, Ergebnisse und Turnierbaum."
        />
        <link
          rel="canonical"
          href={`https://dartcloudapp.de/statistiken/saison/${encodeURIComponent(seasonStageId || '')}`}
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Link
          to="/statistiken"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 transition-colors mb-4"
        >
          <ArrowLeft size={14} /> Statistik-Explorer
        </Link>

        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            <BarChart3 size={30} style={{ color: TEAL }} />
            {title}
          </h1>
          {stats?.matchMode && (
            <p className="text-xs text-gray-500 mt-1">
              Modus: {stats.matchMode === 'sets' ? 'Sätze' : 'Legs'}
            </p>
          )}
        </div>

        <Tabs
          aria-label="Saison-Bereiche"
          selectedKey={selectedTab}
          onSelectionChange={(key) => setSelectedTab(String(key))}
          variant="underlined"
          classNames={{
            tabList: 'gap-6 border-b border-zinc-800 w-full',
            cursor: 'bg-[#3fced1]',
            tab: 'px-0 h-12',
            tabContent: 'group-data-[selected=true]:text-[#3fced1] text-gray-400',
          }}
        >
          <Tab
            key="stats"
            title={
              <span className="flex items-center gap-2">
                <BarChart3 size={15} /> Statistiken
              </span>
            }
          >
            <SeasonStatsTab stats={stats} loading={loadingStats} onPlayerClick={handlePlayerClick} />
          </Tab>
          <Tab
            key="results"
            title={
              <span className="flex items-center gap-2">
                <Swords size={15} /> Ergebnisse
              </span>
            }
          >
            <SeasonResultsTab
              data={resultsData}
              loading={loadingResults}
              onPlayerClick={handlePlayerClick}
            />
          </Tab>
          <Tab
            key="draw"
            title={
              <span className="flex items-center gap-2">
                <GitBranch size={15} /> Bracket
              </span>
            }
          >
            <SeasonDrawTab draw={draw} loading={loadingDraw} onPlayerClick={handlePlayerClick} />
          </Tab>
        </Tabs>
      </div>
    </div>
  );
};

// ============================================================
// Tab: Statistiken
// ============================================================

interface SeasonStatsTabProps {
  stats: SeasonStats | null;
  loading: boolean;
  onPlayerClick: (name: string) => void;
}

const toLeaderboardRows = (
  list: SeasonPlayerStat[] | undefined,
  valueOf: (p: SeasonPlayerStat) => number | string | null | undefined,
  sublabelOf?: (p: SeasonPlayerStat) => string | null,
) => {
  return (Array.isArray(list) ? list : [])
    .filter((p) => p && p.name)
    .map((p) => ({
      name: p.name,
      image: p.image || null,
      value: valueOf(p) ?? null,
      sublabel: sublabelOf ? sublabelOf(p) : null,
    }));
};

const SeasonStatsTab: React.FC<SeasonStatsTabProps> = ({ stats, loading, onPlayerClick }) => {
  const bestAverages = useMemo(() => {
    const r = stats?.rankings;
    // Backend: bestTournamentAverages; älterer Contract: bestAverages
    return r?.bestTournamentAverages || r?.bestAverages || [];
  }, [stats]);

  if (loading) {
    return (
      <div className="pt-6 text-center">
        <Spinner size="lg" style={{ color: TEAL }} />
        <p className="text-sm text-gray-500 mt-4 flex items-center justify-center gap-2">
          <Info size={14} />
          Saison-Statistiken werden berechnet – das kann beim ersten Aufruf etwas dauern...
        </p>
      </div>
    );
  }

  if (!stats) {
    return (
      <Card className="bg-zinc-900 border border-zinc-800 mt-4">
        <CardBody className="p-10 text-center">
          <BarChart3 size={40} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">Keine Statistiken für diese Saison verfügbar</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="pt-4 space-y-6">
      {/* Stat-Karten */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Matches" value={stats.totalMatches ?? null} icon={<Swords size={14} />} />
        <StatCard label="180er" value={stats.total180s ?? null} icon={<Flame size={14} />} accent />
        <StatCard label="140+" value={stats.total140Plus ?? null} icon={<TrendingUp size={14} />} />
        <StatCard
          label="High Finishes"
          value={stats.totalHighFinishes ?? null}
          icon={<Target size={14} />}
        />
      </div>

      {/* Highlights */}
      {(stats.highestCheckout?.value || stats.highestAverage?.value) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stats.highestCheckout?.value ? (
            <StatCard
              label="Höchster Checkout"
              value={stats.highestCheckout.value}
              icon={<Target size={14} />}
              sublabel={stats.highestCheckout.player || null}
              accent
            />
          ) : null}
          {stats.highestAverage?.value ? (
            <StatCard
              label="Bester Match-Average"
              value={Number(stats.highestAverage.value).toFixed(2)}
              icon={<TrendingUp size={14} />}
              sublabel={
                stats.highestAverage.player
                  ? `${stats.highestAverage.player}${stats.highestAverage.opponent ? ` (vs. ${stats.highestAverage.opponent})` : ''}`
                  : null
              }
              accent
            />
          ) : null}
        </div>
      ) : null}

      {/* Leaderboards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <LeaderboardTable
          title="Meiste 180er"
          icon={<Flame size={16} />}
          rows={toLeaderboardRows(
            stats.rankings?.most180s,
            (p) => p.total180s,
            (p) => (p.matches != null ? `${p.matches} Matches` : null),
          )}
          valueLabel="180er"
          onPlayerClick={onPlayerClick}
        />
        <LeaderboardTable
          title="Beste Averages"
          icon={<TrendingUp size={16} />}
          rows={toLeaderboardRows(
            bestAverages,
            (p) => p.average,
            (p) => (p.matches != null ? `${p.matches} Matches` : null),
          )}
          valueLabel="Average"
          decimals={2}
          onPlayerClick={onPlayerClick}
        />
        <LeaderboardTable
          title="Höchste Checkouts"
          icon={<Target size={16} />}
          rows={toLeaderboardRows(
            stats.rankings?.highestCheckouts,
            (p) => p.highestCheckout,
          )}
          valueLabel="Checkout"
          onPlayerClick={onPlayerClick}
        />
      </div>

      {/* High-Finish-Verteilung */}
      <HighFinishBars entries={stats.highFinishList} />
    </div>
  );
};

// ============================================================
// Tab: Ergebnisse (nach Runden gruppiert)
// ============================================================

interface SeasonResultsTabProps {
  data: ExplorerSeasonResultsResponse | null;
  loading: boolean;
  onPlayerClick: (name: string) => void;
}

const SeasonResultsTab: React.FC<SeasonResultsTabProps> = ({ data, loading, onPlayerClick }) => {
  if (loading || !data) {
    return (
      <div className="flex justify-center items-center h-48 pt-4">
        <Spinner size="lg" style={{ color: TEAL }} />
      </div>
    );
  }

  const rounds = (data?.rounds || []).filter((r) => r && Array.isArray(r.matches) && r.matches!.length > 0);

  if (!data || rounds.length === 0) {
    return (
      <Card className="bg-zinc-900 border border-zinc-800 mt-4">
        <CardBody className="p-10 text-center">
          <Swords size={40} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">Keine Ergebnisse für diese Saison verfügbar</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="pt-4 space-y-5">
      {rounds.map((round, idx) => (
        <Card key={round.name || idx} className="bg-zinc-900 border border-zinc-800">
          <CardBody className="p-0">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Trophy size={15} style={{ color: TEAL }} />
                {round.name && round.name !== 'Unknown' ? round.name : 'Runde'}
              </h3>
              <span className="text-xs text-gray-500">{round.matches!.length} Matches</span>
            </div>
            <ul className="divide-y divide-zinc-800/60">
              {round.matches!.map((match, mIdx) => (
                <SeasonResultRow
                  key={match.eventId || mIdx}
                  match={match}
                  onPlayerClick={onPlayerClick}
                />
              ))}
            </ul>
          </CardBody>
        </Card>
      ))}
    </div>
  );
};

const SeasonResultRow: React.FC<{
  match: ExplorerSeasonResult;
  onPlayerClick: (name: string) => void;
}> = ({ match, onPlayerClick }) => {
  const homeWon = match.winner === 1;
  const awayWon = match.winner === 2;

  const renderSide = (
    side: ExplorerSeasonResult['home'],
    won: boolean,
    average: string | number | null | undefined,
    alignRight: boolean,
  ) => {
    const name = statsExplorerService.cleanPlayerName(side?.name);
    const flag = statsExplorerService.getFlagEmoji(side?.countryName);
    return (
      <div className={`flex-1 min-w-0 ${alignRight ? 'text-right' : 'text-left'}`}>
        <button
          onClick={() => onPlayerClick(name)}
          className={`text-sm truncate max-w-full hover:text-[#3fced1] transition-colors ${
            won ? 'font-bold text-white' : 'text-gray-400'
          }`}
        >
          {!alignRight && flag && <span className="mr-1">{flag}</span>}
          {name}
          {alignRight && flag && <span className="ml-1">{flag}</span>}
        </button>
        {average != null && String(average).trim() !== '' && (
          <p className="text-[10px] text-gray-500 tabular-nums">Ø {average}</p>
        )}
      </div>
    );
  };

  return (
    <li className="px-4 py-2.5">
      <div className="flex items-center gap-3">
        {renderSide(match.home, homeWon, match.homeAverage, false)}
        <div className="flex flex-col items-center flex-shrink-0 px-1">
          <span className="text-sm font-bold tabular-nums">
            <span className={homeWon ? '' : 'text-gray-500'}>{match.home?.score ?? '-'}</span>
            <span className="text-gray-600 mx-1">:</span>
            <span className={awayWon ? '' : 'text-gray-500'}>{match.away?.score ?? '-'}</span>
          </span>
          <span className="text-[9px] text-gray-600">
            {statsExplorerService.formatDate(match.startTime)}
          </span>
        </div>
        {renderSide(match.away, awayWon, match.awayAverage, true)}
      </div>
    </li>
  );
};

// ============================================================
// Tab: Bracket (einfache Runden-Spalten)
// ============================================================

interface SeasonDrawTabProps {
  draw: SeasonDraw | null;
  loading: boolean;
  onPlayerClick: (name: string) => void;
}

const SeasonDrawTab: React.FC<SeasonDrawTabProps> = ({ draw, loading, onPlayerClick }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-48 pt-4">
        <Spinner size="lg" style={{ color: TEAL }} />
      </div>
    );
  }

  const rounds = (draw?.rounds || []).filter(
    (r) => r && Array.isArray(r.matches) && r.matches!.length > 0,
  );

  if (rounds.length === 0) {
    return (
      <Card className="bg-zinc-900 border border-zinc-800 mt-4">
        <CardBody className="p-10 text-center">
          <GitBranch size={40} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">Kein Bracket für diese Saison verfügbar</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="pt-4">
      {draw?.totalParticipants != null && (
        <Chip size="sm" variant="flat" className="bg-zinc-800 text-gray-300 mb-4">
          {draw.totalParticipants} Teilnehmer · {rounds.length} Runden
        </Chip>
      )}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max items-start">
          {rounds.map((round, idx) => (
            <div key={round.roundNumber ?? idx} className="w-64 flex-shrink-0">
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2 px-1">
                {round.roundName || `Runde ${idx + 1}`}
              </p>
              <div className="space-y-2">
                {round.matches!.map((match, mIdx) => (
                  <DrawMatchCard
                    key={match.eventId || mIdx}
                    match={match}
                    onPlayerClick={onPlayerClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DrawMatchCard: React.FC<{
  match: DrawMatch;
  onPlayerClick: (name: string) => void;
}> = ({ match, onPlayerClick }) => {
  const renderSlot = (side: DrawMatch['home']) => {
    const name = side?.name ? statsExplorerService.cleanPlayerName(side.name) : null;
    const isWinner = side?.isWinner === true;
    const potential = (side?.potentialPlayers || []).filter(Boolean);

    return (
      <div className="flex items-center justify-between gap-2 px-3 py-1.5">
        {name ? (
          <button
            onClick={() => onPlayerClick(name)}
            className={`text-xs truncate text-left hover:text-[#3fced1] transition-colors ${
              isWinner ? 'font-bold text-white' : 'text-gray-400'
            }`}
          >
            {isWinner && <Trophy size={10} className="inline mr-1" style={{ color: TEAL }} />}
            {name}
            {side?.seed && <span className="text-gray-600 ml-1">({side.seed})</span>}
          </button>
        ) : (
          <span
            className="text-xs text-gray-600 italic truncate"
            title={potential.length > 0 ? `Mögliche Spieler: ${potential.join(', ')}` : undefined}
          >
            {potential.length > 0 ? potential.slice(0, 2).join(' / ') + (potential.length > 2 ? ' …' : '') : 'TBD'}
          </span>
        )}
        <span
          className={`text-xs font-bold tabular-nums flex-shrink-0 ${
            isWinner ? 'text-white' : 'text-gray-500'
          }`}
        >
          {side?.score != null && String(side.score).trim() !== '' ? side.score : '-'}
        </span>
      </div>
    );
  };

  return (
    <div
      className={`bg-zinc-900 border rounded-lg divide-y divide-zinc-800/60 ${
        match.status === 'finished' ? 'border-zinc-800' : 'border-zinc-800/50'
      }`}
    >
      {renderSlot(match.home)}
      {renderSlot(match.away)}
      {match.startTime && match.status !== 'finished' && (
        <p className="px-3 py-1 text-[9px] text-gray-600">
          {statsExplorerService.formatDateTime(match.startTime)}
        </p>
      )}
    </div>
  );
};

export default SeasonStatsPage;
